import { useContext, useEffect } from "react";
import { useGameList } from "../../api/hooks/games";
import GameInfo from "../../components/GameInfo";
import Button from "../../components/Button";
import { useNavigate } from "react-router-dom";
import { AspectRatio, Divider } from "@mui/joy";
import styles from "./Game.module.css";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination } from "swiper/modules";

// Import Swiper styles
import "swiper/css";
import "swiper/css/pagination";
import GroupsBlock from "../../components/GroupsBlock";
import PageHeader from "../../components/PageHeader";
import GameTag from "../../components/STags/GameTag";
import { StrToCurrency } from "../../api/ApiGame";
import SkeletonCard from "../../components/SkeletonCard";
import Contexts from "../../contexts/Contexts";
import { useTranslation } from "react-i18next";
import MiniGame from "../../components/MiniGame";
import CaixaStrip from "../../components/CaixaStrip";
import WelcomeBanner from "../../components/WelcomeBanner";
import AffiliateRefStrip from "../../components/Affiliate/AffiliateRefStrip";

const Games = () => {
  const { gameList, fetchGameList, loading } = useGameList();
  const navigate = useNavigate();
  const { screenSize, globalGameFilter, setGlobalGameFilter } = useContext(Contexts);
  const { t } = useTranslation();

  useEffect(() => {
    // Fetch game list on mount and when filter changes
    // This is non-blocking - page renders immediately
    if (globalGameFilter) {
      fetchGameList(globalGameFilter);
    } else {
      fetchGameList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalGameFilter]);

  const firstGame = gameList[0] || null;

  return (
    <div className={styles.container}>
      <WelcomeBanner upcomingDraw={firstGame} />
      <CaixaStrip />
      <PageHeader title={t("games.hotTitle")} subtitle={t("games.subtitle")} />
      {/* CLS fix: reserve carousel height while loading to prevent MiniGame section shift */}
      <div className={styles.slider} style={loading && gameList.length === 0 ? { minHeight: 260 } : undefined}>
        <Swiper
          // Swiper manages slide layout (activeIndex/snapGrid) imperatively and does
          // NOT recompute it when its children change. On a filter change the slide set
          // swaps but the carousel kept a stale active slide (e.g. centered the wrong
          // game). Keying on the rendered gameList ids forces a clean remount exactly
          // when the data swaps, so a fresh Swiper re-inits from the current slides.
          key={gameList.map((g) => g.game.gameId).join('-')}
          initialSlide={Math.min(1, gameList.length - 1)}
          spaceBetween={10}
          slidesPerView={screenSize.isDesktop ? 3 : 1.2}
          pagination={true}
          // loop={true}
          centeredSlides={true}
          modules={[Pagination]}
          // onSlideChange={() => console.log("slide change")}
          // onSwiper={(swiper: any) => console.log(swiper)}
        >
          {gameList.map((game, index) => (
            <SwiperSlide key={game.game.gameId}>
              <AspectRatio
                variant="plain"
                ratio="722/416"
                sx={{
                  width: "100%",
                  borderRadius: '16px',
                  overflow: 'hidden',
                }}
              >
                <div>
                  <div className={styles.sliderItem}>
                    {/* Phase 4 Step 1: converted from CSS backgroundImage to <img> so the
                        preload scanner can discover the LCP image */}
                    <picture className={styles.sliderBg}>
                      <source srcSet={`/images/Banner${game.game.gameId}.avif`} type="image/avif" />
                      <source srcSet={`/images/Banner${game.game.gameId}.webp`} type="image/webp" />
                      <img
                        src={`/images/Banner${game.game.gameId}.png`}
                        alt={game.game.gameName}
                        width={722}
                        height={416}
                        // initialSlide is 1 (centered): slides 0-2 sit in the first
                        // viewport — 1 fills it on mobile and IS the LCP element, 0 and 2
                        // flank it (fully visible on desktop). Eager-load all three; high
                        // priority only for the two LCP candidates (0 desktop, 1 mobile).
                        loading={index <= 2 ? "eager" : "lazy"}
                        fetchPriority={index <= 1 ? "high" : "auto"}
                        className={styles.sliderBgImg}
                        decoding={index <= 1 ? "sync" : "async"}
                      />
                    </picture>
                    <div className={styles.sliderTag} >
                      <GameTag
                        light
                        name={game.game.gameName}
                        tag={game.game.gameTag}
                      />
                    </div>
                    <div className={styles.sliderMainPrize}>
                      {StrToCurrency(game.draw ? game.draw.mainPrize : 0)}
                    </div>
                    {game.draw?.isClosed && (
                      <div className={styles.sliderClosedBadge}>🔒 Vendas encerradas</div>
                    )}
                    <div className={styles.sliderButtons}>
                      <Button
                        type="light"
                        width="48%"
                        disabled={!!game.draw?.isClosed}
                        onClick={() =>
                          !game.draw?.isClosed && navigate(
                            game.draw ? `/games/${game.draw.drawId}` : `/groups/`
                          )
                        }
                      >
                        {t("games.makeBet")}
                      </Button>
                      <Button width="48%" disabled={!!game.draw?.isClosed} onClick={() => { if (!game.draw?.isClosed) { setGlobalGameFilter(game.game.gameId); navigate('/groups/', { state: { gameFilter: game.game.gameId } }); } }}>
                        {t("games.groupBetting")}
                      </Button>
                    </div>
                  </div>
                </div>
              </AspectRatio>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
      <AffiliateRefStrip />
      {!loading && gameList.length === 0 && (
        <div className={styles.emptyState}>
          <picture>
            <source srcSet="/images/mockingBird.webp" type="image/webp" />
            <img src="/images/mockingBird.png" alt="" className={styles.emptyBird} width={140} height={140} loading="lazy" decoding="async" />
          </picture>
          <p className={styles.emptyTitle}>{t("games.noDrawsTitle")}</p>
          <p className={styles.emptySubtitle}>{t("games.noDrawsSubtitle")}</p>
        </div>
      )}
      <div className={styles.cardContainer}>
        {loading && Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className={styles.cardSlot}>
            <SkeletonCard length={1} loading={true} />
          </div>
        ))}
        {gameList.map((game, i) => (
          <div key={game.game.gameId} className={styles.cardSlot}>
          <div className="card card-2-margin">
            <GameInfo
              showDetail
              size="s"
              gameInfo={{
                game: game.game,
                draw: game.draw,
              }}
              rightButton={
                game.draw ? (
                  <Button
                    size="s"
                    width="80px"
                    onClick={() => navigate(`/games/${game.draw.drawId}`)}
                  >
                    {t("games.bet")}
                  </Button>
                ) : (
                  <Button
                    size="s"
                    width="80px"
                    onClick={() => { setGlobalGameFilter(game.game.gameId); navigate('/groups/', { state: { gameFilter: game.game.gameId } }); }}
                  >
                    {t("games.groupBetting")}
                  </Button>
                )
              }
            />
            <Divider />
            <GroupsBlock
              label={t("games.popularGroups")}
              labelColor={"#9AA9B3"}
              labelSize={16}
              initialGroups={game.popularGroups}
              gameId={game.game.gameId}
            />
          </div>
          </div>
        ))}
      </div>
      <MiniGame />
    </div>
  );
};
export default Games;
